import { formatDate, formatMoney, PAYMENT_METHOD_LABELS } from '../lib/format';

/**
 * Read-only view of a pending charge attempt persisted by
 * `createPendingPaymentStore` (`client/src/gym/lib/pending-payment.js`).
 * Purely presentational — `PaymentForm` owns the state machine and decides
 * when to show this instead of the editable form.
 *
 * Design (spec "Administrador", cobro paragraph): while there's no
 * definitive answer for a charge, the admin can only retry the EXACT same
 * payload/UUID, never edit it or start a different one — so this never
 * exposes inputs, only the stored signature as text plus a single retry
 * action. `phase === 'conflict'` (409 `idempotency_conflict`) additionally
 * disables retrying: the same key already resolved to a DIFFERENT payload
 * server-side, so blindly resending it would only repeat the same conflict —
 * it needs a human to look at the occupying record first.
 *
 * A single `role="alert"` paragraph carries both the fixed headline and the
 * latest server/network message (parenthetically) on purpose: two separate
 * alert elements would make `getByRole('alert')` ambiguous for callers that,
 * correctly, expect exactly one.
 *
 * Fix round 1, item 3: the pending attempt's OWN `actorId` (whoever started
 * it) can differ from the admin currently looking at it — the store is
 * intentionally shared across logins on the same browser (spec: "si cambió
 * el administrador, se identifica como intento previo"). Surfacing that
 * distinction here (rather than silently letting anyone assume they started
 * it) is why `record` — not just `record.payload` — and the CURRENT
 * `actorId` are both passed in.
 */
export function PendingPaymentBanner({
  record, actorId, phase, error, retrying, onRetry,
}) {
  const { payload } = record;
  const methodLabel = PAYMENT_METHOD_LABELS[payload.method] || payload.method;
  const isConflict = phase === 'conflict';
  const isOtherAdmin = Boolean(actorId) && Boolean(record.actorId) && record.actorId !== actorId;
  const headline = isConflict
    ? 'Este cobro quedó en conflicto de idempotencia con un registro distinto: no se puede reintentar solo, hace falta investigar la firma ocupante.'
    : 'Hay un resultado pendiente de este cobro: no sabemos si se acreditó en el servidor. Reintentá exactamente el mismo cobro; no se puede registrar otro hasta resolverlo.';

  return (
    <div className="gym-admin-pending-payment">
      <p role="alert" className="gym-form__error">
        {headline}
        {error ? ` (${error})` : ''}
      </p>
      {isOtherAdmin && (
        <p className="gym-admin-pending-payment__actor">Intento previo de otro administrador</p>
      )}
      <p className="gym-admin-pending-payment__details">
        {formatMoney(Number(payload.amount))} · {formatDate(payload.date)} · {methodLabel}
      </p>
      <div className="gym-admin-form-actions">
        <button
          type="button"
          className="gym-button gym-button--primary"
          onClick={onRetry}
          disabled={retrying || isConflict}
        >
          {retrying ? 'Reintentando…' : 'Reintentar mismo cobro'}
        </button>
        <button type="button" className="gym-button gym-button--secondary" disabled>
          Registrar otro cobro
        </button>
      </div>
    </div>
  );
}
